import { useMemo, useState, Fragment } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Clock, Users as UsersIcon, Timer, FileDown, HardHat, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { fmtDate } from "@/lib/format";

// Friendly role labels — keep in sync with `ROLE_LABELS` in pages/Users.jsx.
const ROLE_LABELS = {
  admin: "Admin",
  sales: "Sales",
  warehouse: "Warehouse",
  it_support: "IT Support Engineering",
  finance: "Finance",
  office_support: "Office Support",
};
function roleLabel(r) {
  return ROLE_LABELS[r] || (r ? r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");
}
const ROLE_PILL_CLASS = {
  admin: "bg-red-50 text-red-700 ring-red-200",
  sales: "bg-amber-50 text-amber-800 ring-amber-200",
  warehouse: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  it_support: "bg-blue-50 text-blue-700 ring-blue-200",
  finance: "bg-violet-50 text-violet-700 ring-violet-200",
  office_support: "bg-slate-50 text-slate-700 ring-slate-200",
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

// Parse "HH:MM" -> minutes since midnight (or null)
function parseHHMM(t) {
  if (!t || typeof t !== "string") return null;
  const parts = t.trim().split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}
function computeTotals(start, finish) {
  const s = parseHHMM(start);
  const f = parseHHMM(finish);
  if (s == null || f == null) return { minutes: 0, hours: 0 };
  let delta = f - s;
  if (delta < 0) delta += 24 * 60; // crosses midnight
  return { minutes: delta, hours: Math.round((delta / 60) * 100) / 100 };
}

const emptyForm = {
  client_id: "",
  engineer_user_id: "",
  attendance_date: todayISO(),
  time_start: "09:00",
  time_finish: "17:00",
  notes: "",
};

export default function Attendance() {
  const qc = useQueryClient();

  const [month, setMonth] = useState(currentMonth());
  const [clientFilter, setClientFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [expandedClients, setExpandedClients] = useState({});
  const toggleClient = (cid) =>
    setExpandedClients((prev) => ({ ...prev, [cid]: !prev[cid] }));

  const { data: clients = [] } = useQuery({
    queryKey: ["clients-for-attendance"],
    queryFn: () => api.listClients(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users-directory"],
    queryFn: () => api.usersDirectory(),
  });

  // Engineers scoped to the currently-selected client's site engineers.
  // Also always include IT-support and warehouse staff (they are the ops
  // pool that can be dispatched to any client, whether or not the client
  // has an explicit `site_engineer_ids` list). Falls back to the full
  // active user list when no client is picked yet.
  const engineersForClient = useMemo(() => {
    const sel = clients.find((c) => c.id === form.client_id);
    const ids = (sel && Array.isArray(sel.site_engineer_ids)) ? sel.site_engineer_ids : [];
    if (ids.length === 0) return users;
    const set = new Set(ids);
    return users.filter(
      (u) => set.has(u.id) || u.role === "it_support" || u.role === "warehouse",
    );
  }, [clients, users, form.client_id]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["attendance", month, clientFilter],
    queryFn: () =>
      api.listAttendance({
        ...(month ? { month } : {}),
        ...(clientFilter && clientFilter !== "all" ? { client_id: clientFilter } : {}),
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ["attendance-stats", month],
    queryFn: () => api.attendanceStats(month),
  });

  const totals = useMemo(() => {
    let minutes = 0;
    let sessions = rows.length;
    const clientIds = new Set();
    for (const r of rows) {
      minutes += Number(r.total_minutes || 0);
      if (r.client_id) clientIds.add(r.client_id);
    }
    return {
      minutes,
      hours: Math.round((minutes / 60) * 100) / 100,
      sessions,
      clients: clientIds.size,
    };
  }, [rows]);

  // Group sessions by client so the list stays short even with 30+ entries.
  // Each group carries its own subtotal (sessions / minutes / hours).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const cid = r.client_id || "_none";
      const name = r.client_snapshot?.name || "—";
      if (!map.has(cid)) {
        map.set(cid, { client_id: cid, name, sessions: [], minutes: 0 });
      }
      const g = map.get(cid);
      g.sessions.push(r);
      g.minutes += Number(r.total_minutes || 0);
    }
    // Sort each group's sessions newest first.
    for (const g of map.values()) {
      g.sessions.sort((a, b) => (b.attendance_date || "").localeCompare(a.attendance_date || ""));
      g.hours = Math.round((g.minutes / 60) * 100) / 100;
    }
    // Sort groups by name.
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const previewTotals = useMemo(
    () => computeTotals(form.time_start, form.time_finish),
    [form.time_start, form.time_finish],
  );

  const saveMut = useMutation({
    mutationFn: (body) =>
      editing ? api.updateAttendance(editing.id, body) : api.createAttendance(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-stats"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      toast.success(editing ? "Attendance updated" : "Session logged");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteAttendance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-stats"] });
      setConfirmDeleteId(null);
      toast.success("Session deleted");
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, attendance_date: todayISO() });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      client_id: r.client_id || "",
      engineer_user_id: r.engineer_user_id || "",
      attendance_date: r.attendance_date || todayISO(),
      time_start: r.time_start || "09:00",
      time_finish: r.time_finish || "17:00",
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = () => {
    if (!form.client_id) {
      toast.error("Please select a client");
      return;
    }
    if (!form.attendance_date) {
      toast.error("Date is required");
      return;
    }
    if (parseHHMM(form.time_start) == null || parseHHMM(form.time_finish) == null) {
      toast.error("Times must be in HH:MM format");
      return;
    }
    if (previewTotals.minutes <= 0) {
      toast.error("End time must be after start time");
      return;
    }
    saveMut.mutate({ ...form, engineer_user_id: form.engineer_user_id || null });
  };

  const downloadMonthlyPdf = () => {
    api
      .openAttendancePdf({
        month,
        ...(clientFilter && clientFilter !== "all" ? { client_id: clientFilter } : {}),
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "PDF failed"));
  };
  const openMonthlyPdfPreview = () => setPdfPreviewOpen(true);
  const pdfParams = {
    month,
    ...(clientFilter && clientFilter !== "all" ? { client_id: clientFilter } : {}),
  };

  return (
    <div data-testid="attendance-page">
      <PageHeader
        overline="time tracking"
        title="Client Attendance"
        actions={
          <div className="flex items-center gap-2">
            <Input
              data-testid="attendance-month-filter"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-40"
            />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger data-testid="attendance-client-filter" className="w-48">
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="attendance-pdf-btn"
              variant="outline"
              onClick={openMonthlyPdfPreview}
              disabled={rows.length === 0}
              className="border-slate-300"
              title="Preview the monthly PDF before downloading"
            >
              <FileDown className="size-4 mr-1.5" /> Monthly PDF
            </Button>
            <Button
              data-testid="attendance-new-btn"
              onClick={openCreate}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> Log session
            </Button>
          </div>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KPI
          testid="attendance-kpi-sessions"
          label="Sessions"
          icon={Clock}
          value={totals.sessions}
          hint={`in ${month}`}
          tone="slate"
        />
        <KPI
          testid="attendance-kpi-clients"
          label="Clients seen"
          icon={UsersIcon}
          value={totals.clients}
          hint="unique"
          tone="emerald"
        />
        <KPI
          testid="attendance-kpi-minutes"
          label="Total minutes"
          icon={Timer}
          value={totals.minutes}
          hint="raw"
          tone="amber"
          mono
        />
        <KPI
          testid="attendance-kpi-hours"
          label="Total hours"
          icon={Clock}
          value={totals.hours}
          hint="minutes ÷ 60"
          tone="red"
          mono
        />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No sessions logged this month"
          hint="Click 'Log session' to record a client visit"
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto mb-6">
          <div className="px-3 py-2 border-b border-border bg-slate-50/60 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {grouped.length} client{grouped.length === 1 ? "" : "s"} · {rows.length} sessions
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="attendance-expand-all"
                className="h-7 text-xs"
                onClick={() => {
                  const all = {};
                  for (const g of grouped) all[g.client_id] = true;
                  setExpandedClients(all);
                }}
              >
                Expand all
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="attendance-collapse-all"
                className="h-7 text-xs"
                onClick={() => setExpandedClients({})}
              >
                Collapse all
              </Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline w-8"></TableHead>
                <TableHead className="overline">Client</TableHead>
                <TableHead className="overline text-right">Sessions</TableHead>
                <TableHead className="overline text-right">Minutes</TableHead>
                <TableHead className="overline text-right">Hours</TableHead>
                <TableHead className="overline text-right w-24">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped.map((g) => {
                const isOpen = !!expandedClients[g.client_id];
                return (
                  <Fragment key={g.client_id}>
                    <TableRow
                      key={`grp-${g.client_id}`}
                      data-testid={`attendance-client-row-${g.client_id}`}
                      className="cursor-pointer hover:bg-slate-50/60 border-b border-border"
                      onClick={() => toggleClient(g.client_id)}
                    >
                      <TableCell className="w-8">
                        {isOpen
                          ? <ChevronDown className="size-4 text-slate-500" />
                          : <ChevronRight className="size-4 text-slate-500" />}
                      </TableCell>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell className="text-right mono text-sm">{g.sessions.length}</TableCell>
                      <TableCell className="text-right mono text-sm">{g.minutes}</TableCell>
                      <TableCell className="text-right mono text-sm font-semibold">
                        {g.hours.toFixed(2)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow key={`grp-${g.client_id}-details`} className="hover:bg-transparent border-b border-border">
                        <TableCell colSpan={6} className="p-0 bg-slate-50/30">
                          <div className="pl-8 pr-2 py-2">
                            <Table>
                              <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                  <TableHead className="overline">Date</TableHead>
                                  <TableHead className="overline">Engineer</TableHead>
                                  <TableHead className="overline">Start</TableHead>
                                  <TableHead className="overline">Finish</TableHead>
                                  <TableHead className="overline text-right">Min</TableHead>
                                  <TableHead className="overline text-right">Hrs</TableHead>
                                  <TableHead className="overline">Notes</TableHead>
                                  <TableHead className="overline text-right w-24">&nbsp;</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {g.sessions.map((r) => (
                                  <TableRow key={r.id} data-testid={`attendance-row-${r.id}`}>
                                    <TableCell className="mono text-xs">{fmtDate(r.attendance_date)}</TableCell>
                                    <TableCell className="text-xs">
                                      {r.engineer_snapshot?.name ? (
                                        <span className="inline-flex items-center gap-1">
                                          <HardHat className="size-3 text-slate-500" />
                                          {r.engineer_snapshot.name}
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="mono text-xs">{r.time_start}</TableCell>
                                    <TableCell className="mono text-xs">{r.time_finish}</TableCell>
                                    <TableCell className="text-right mono">{r.total_minutes}</TableCell>
                                    <TableCell className="text-right mono font-semibold">
                                      {Number(r.total_hours || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate">
                                      {r.notes || "—"}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <div className="inline-flex gap-1">
                                        <Button
                                          data-testid={`attendance-edit-${r.id}`}
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0"
                                          onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                                        >
                                          <Pencil className="size-3.5" />
                                        </Button>
                                        <Button
                                          data-testid={`attendance-delete-${r.id}`}
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(r.id); }}
                                        >
                                          <Trash2 className="size-3.5" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Monthly rollup by client */}
      {stats && stats.by_client && stats.by_client.length > 0 && (
        <div className="border border-border rounded-md bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-slate-50 flex items-center justify-between">
            <div>
              <p className="overline">Monthly rollup — by client</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {stats.month} · {stats.total_sessions} sessions · {stats.total_hours} hrs total
              </p>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Client</TableHead>
                <TableHead className="overline text-right">Sessions</TableHead>
                <TableHead className="overline text-right">Minutes</TableHead>
                <TableHead className="overline text-right">Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.by_client.map((b) => (
                <TableRow
                  key={b.client_id}
                  data-testid={`attendance-rollup-${b.client_id}`}
                >
                  <TableCell className="font-medium">{b.client_name}</TableCell>
                  <TableCell className="text-right mono">{b.sessions}</TableCell>
                  <TableCell className="text-right mono">{b.minutes}</TableCell>
                  <TableCell className="text-right mono font-semibold">
                    {Number(b.hours || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            setEditing(null);
            setForm(emptyForm);
          }
          setOpen(v);
        }}
      >
        <DialogContent data-testid="attendance-dialog" className="sm:max-w-none max-w-none w-[min(96vw,1400px)] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit session" : "Log a client session"}</DialogTitle>
            <DialogDescription className="text-xs">
              Total hours are computed as minutes ÷ 60. Sessions that cross midnight are handled automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Client *</Label>
              <Select
                value={form.client_id}
                onValueChange={(v) =>
                  setForm({ ...form, client_id: v, engineer_user_id: "" })
                }
              >
                <SelectTrigger data-testid="attendance-form-client">
                  <SelectValue placeholder="Select a client…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">
                Engineer / performed by
                {form.client_id && engineersForClient.length > 0 &&
                 engineersForClient.length < users.length && (
                  <span className="ml-1.5 text-[9px] font-normal text-emerald-700">
                    (scoped to this client&apos;s site engineers)
                  </span>
                )}
              </Label>
              <Select
                value={form.engineer_user_id || ""}
                onValueChange={(v) =>
                  setForm({ ...form, engineer_user_id: v === "_none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="attendance-form-engineer">
                  <SelectValue placeholder="Select an engineer (optional)…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="_none">— None —</SelectItem>
                  {engineersForClient.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <span className="flex items-center gap-2">
                        <span>{u.name}</span>
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold ring-1 ring-inset ${ROLE_PILL_CLASS[u.role] || "bg-slate-50 text-slate-700 ring-slate-200"}`}
                        >
                          {roleLabel(u.role)}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="overline">Date *</Label>
                <Input
                  data-testid="attendance-form-date"
                  type="date"
                  value={form.attendance_date}
                  onChange={(e) => setForm({ ...form, attendance_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Time start *</Label>
                <Input
                  data-testid="attendance-form-start"
                  type="time"
                  value={form.time_start}
                  onChange={(e) => setForm({ ...form, time_start: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Time finish *</Label>
                <Input
                  data-testid="attendance-form-finish"
                  type="time"
                  value={form.time_finish}
                  onChange={(e) => setForm({ ...form, time_finish: e.target.value })}
                />
              </div>
            </div>

            {/* Live preview of totals */}
            <div
              data-testid="attendance-form-preview"
              className="rounded-md border border-red-200 bg-red-50 p-3 flex items-center justify-between"
            >
              <div className="text-xs text-red-900">
                <div className="font-semibold">Session length</div>
                <div className="text-red-800/70">Auto-calculated from start & finish</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-red-700">
                  minutes
                </div>
                <div
                  data-testid="attendance-preview-minutes"
                  className="mono font-bold text-lg leading-none text-red-900"
                >
                  {previewTotals.minutes}
                </div>
                <div className="text-[10px] text-red-800/70 mt-1">
                  ={" "}
                  <span
                    data-testid="attendance-preview-hours"
                    className="mono font-semibold"
                  >
                    {previewTotals.hours.toFixed(2)}
                  </span>{" "}
                  hours
                </div>
              </div>
            </div>

            <div>
              <Label className="overline">Notes (optional)</Label>
              <Textarea
                data-testid="attendance-form-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. on-site troubleshooting router restart"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="attendance-form-cancel"
            >
              Cancel
            </Button>
            <Button
              data-testid="attendance-form-save"
              disabled={saveMut.isPending}
              className="bg-primary hover:bg-primary/90"
              onClick={submit}
            >
              {saveMut.isPending ? "Saving…" : editing ? "Save changes" : "Log session"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDeleteId}
        onOpenChange={(v) => !v && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>This can&apos;t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="attendance-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="attendance-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(confirmDeleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        title={`Attendance ${month}${clientFilter && clientFilter !== "all"
          ? " · " + (clients.find((c) => c.id === clientFilter)?.name || "")
          : ""}`}
        filename={`attendance-${month}.pdf`}
        fetchBlob={pdfPreviewOpen ? () => api.fetchAttendancePdfBlob(pdfParams) : null}
      />
    </div>
  );
}

function KPI({ testid, label, icon: Icon, value, hint, tone, mono }) {
  const toneMap = {
    slate: "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    red: "border-red-200 bg-red-50",
  };
  const iconMap = {
    slate: "text-slate-700 bg-slate-100",
    emerald: "text-emerald-700 bg-emerald-100",
    amber: "text-amber-800 bg-amber-100",
    red: "text-red-700 bg-red-100",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 flex items-center justify-between ${toneMap[tone]}`}
    >
      <div>
        <p className="overline">{label}</p>
        <p
          className={`text-2xl font-bold text-foreground mt-1 ${mono ? "font-mono" : ""}`}
        >
          {value}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>
      </div>
      <span className={`size-10 grid place-items-center rounded-full ${iconMap[tone]}`}>
        <Icon className="size-5" />
      </span>
    </div>
  );
}
